import React, { useId } from "react"
import { cn } from "../utils"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { VisuallyHidden } from "../visually-hidden"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderOverflowScroll } from "./page-secondary-header-overflow-scroll"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabsProps = {
  accessibleName: string
} & TabsPrimitive.List.Props

// =============================================================================
// Styles
// =============================================================================

// Pull tabs down to align the active tab's underline with the header's bottom
// border
const pageSecondaryHeaderTabsContainerStyles = [
  "-mb-[calc(var(--page-header-padding)+var(--page-secondary-header-overflow-scroll-padding)+var(--page-header-bottom-border-width))]",
  "-mt-2 @xl/page-header:-mt-4 @6xl/page-header:mt-0"
]

const pageSecondaryHeaderTabsStyles = ["flex items-center gap-6 w-max"]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tabs (panel-switching).
 *
 * Renders Base UI's `Tabs.List` (role="tablist") for switching between panels on
 * the same page. Compose with `PageSecondaryHeader.Tab` for each tab, and place
 * the corresponding `PageSecondaryHeader.TabPanel`s in the page content.
 *
 * **Requires a `PageSecondaryHeader.TabsRoot` ancestor** (Base UI's `Tabs.Root`)
 * wrapping both the page header and the panels, since they live in different
 * parts of the tree but share active-tab state via React context.
 *
 * For navigation links that *look* like tabs (cross-page navigation, not
 * panel-switching), use `PageSecondaryHeader.TabLinks` instead, which renders a
 * `<nav>` landmark with `<a>` elements and `aria-current="page"`.
 */
export function PageSecondaryHeaderTabs({
  accessibleName,
  children,
  className,
  ...props
}: PageSecondaryHeaderTabsProps) {
  const accessibleNameId = useId()

  return (
    <PageSecondaryHeaderCenterContent>
      <PageSecondaryHeaderOverflowScroll
        className={cn(pageSecondaryHeaderTabsContainerStyles)}
      >
        <TabsPrimitive.List
          aria-labelledby={accessibleNameId}
          className={cn(pageSecondaryHeaderTabsStyles, className)}
          data-slot="secondary-tabs"
          {...props}
        >
          <VisuallyHidden id={accessibleNameId}>
            {accessibleName}
          </VisuallyHidden>
          {children}
        </TabsPrimitive.List>
      </PageSecondaryHeaderOverflowScroll>
    </PageSecondaryHeaderCenterContent>
  )
}
